/**
 * FaxDeliverySection — Shows fax delivery tracking info.
 * Supports multi-jurisdiction: renders each fax_deliveries[] entry separately.
 * Falls back to legacy fax_delivery (single object) for backward compat.
 */
import React, { useState, useEffect } from 'react';
import { FileText, RefreshCw, CheckCircle, XCircle, Loader2, Send, AlertTriangle, Download } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTimeET } from '../../utils/formatters';
import FaxResponseDropzone from './FaxResponseDropzone';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const STATUS_CONFIG = {
  sending:        { label: 'Sending',        color: 'bg-blue-100 text-blue-700',   icon: Loader2, animate: true },
  sent:           { label: 'Sent',           color: 'bg-green-100 text-green-700',  icon: CheckCircle },
  received:       { label: 'Received',       color: 'bg-teal-100 text-teal-700',    icon: Download },
  failed:         { label: 'Failed',         color: 'bg-red-100 text-red-700',      icon: XCircle },
  not_configured: { label: 'Not Configured', color: 'bg-amber-100 text-amber-700',  icon: AlertTriangle },
};

// SES delivery status configs — shown as a secondary badge
const SES_STATUS_CONFIG = {
  accepted:  { label: 'SES Accepted',  color: 'bg-sky-50 text-sky-700 border-sky-200',       icon: CheckCircle },
  delivered: { label: 'Delivered to Gateway', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
  bounced:   { label: 'Bounced',       color: 'bg-red-50 text-red-700 border-red-200',       icon: XCircle },
  rejected:  { label: 'Rejected',      color: 'bg-red-50 text-red-700 border-red-200',       icon: XCircle },
  complained:{ label: 'Complained',    color: 'bg-orange-50 text-orange-700 border-orange-200', icon: AlertTriangle },
  failed:    { label: 'SES Failed',    color: 'bg-red-50 text-red-700 border-red-200',       icon: XCircle },
  // Event-type names from SES (used in timeline)
  send:      { label: 'Accepted by SES', color: 'bg-sky-50 text-sky-700 border-sky-200',     icon: Send },
  delivery:  { label: 'Delivered to Gateway', color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
  bounce:    { label: 'Bounced',       color: 'bg-red-50 text-red-700 border-red-200',       icon: XCircle },
  reject:    { label: 'Rejected',      color: 'bg-red-50 text-red-700 border-red-200',       icon: XCircle },
  complaint: { label: 'Complained',    color: 'bg-orange-50 text-orange-700 border-orange-200', icon: AlertTriangle },
};

// Accepted formats / 15MB limit live in FaxResponseDropzone (mirrors the API).

// ── Single fax entry card ──
const FaxEntryCard = ({ entry, orderId, currentUser, onUpdate, onEntriesUpdate, isOnly }) => {
  const [isResending, setIsResending] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showTracking, setShowTracking] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [isLoadingTracking, setIsLoadingTracking] = useState(false);

  const statusCfg = STATUS_CONFIG[entry.status] || STATUS_CONFIG.sending;
  const StatusIcon = statusCfg.icon;
  const canAction = ['admin', 'tax_search_team_leader', 'tax_processor', 'processor', 'team_leader', 'qc_specialist'].includes(currentUser?.role);
  const isClosed = entry.status === 'received';
  const canReceive = canAction && ['sent', 'failed'].includes(entry.status);
  const canResend = canAction && !['sending', 'received'].includes(entry.status);

  const handleResend = async () => {
    if (isResending) return;
    setIsResending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/orders/${orderId}/fax/resend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ fax_entry_id: entry.id }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Fax resent successfully (attempt #${data.fax_delivery?.attempt_count || '?'})`);
        if (data.fax_deliveries && onEntriesUpdate) onEntriesUpdate(data.fax_deliveries);
        if (onUpdate) onUpdate();
      } else {
        toast.error(data.detail || data.error || 'Fax resend failed');
      }
    } catch (err) {
      toast.error('Network error resending fax');
    } finally {
      setIsResending(false);
    }
  };

  const handleFetchTracking = async () => {
    if (isLoadingTracking) return;
    setIsLoadingTracking(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/orders/${orderId}/fax-tracking`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const match = (data.fax_deliveries || []).find(fd => fd.fax_delivery_id === entry.id);
        setTrackingData(match || null);
        setShowTracking(true);
      } else {
        toast.error('Failed to fetch tracking data');
      }
    } catch {
      toast.error('Network error fetching tracking');
    } finally {
      setIsLoadingTracking(false);
    }
  };

  const handleViewPdf = async (fileId) => {
    if (!fileId) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/orders/${orderId}/files/${fileId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const blob = await res.blob();
        window.open(URL.createObjectURL(blob), '_blank');
      } else { toast.error('Failed to load PDF'); }
    } catch { toast.error('Network error loading PDF'); }
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '—';
    try {
      return formatDateTimeET(isoStr) + ' ET';
    } catch { return isoStr; }
  };

  const jurisdictionLabel = entry.jurisdiction_name || (isOnly ? '' : 'Unknown');

  return (
    <div className="border rounded-lg p-3 bg-white" data-testid={`fax-entry-${entry.id || 'legacy'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          {jurisdictionLabel && (
            <span className="text-xs font-semibold text-slate-700 truncate">{jurisdictionLabel}</span>
          )}
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${statusCfg.color}`}
            data-testid="fax-delivery-status-badge"
          >
            <StatusIcon className={`w-3 h-3 ${statusCfg.animate ? 'animate-spin' : ''}`} />
            {statusCfg.label}
          </span>
        </div>
        {!isClosed && (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {canReceive && (
              <button
                onClick={() => setShowUpload(true)}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded border border-teal-300 bg-white hover:bg-teal-50 text-teal-700 transition-colors"
                data-testid="fax-received-button"
              >
                <Download className="w-3 h-3" />
                Received
              </button>
            )}
            {canResend && (
              <button
                onClick={handleResend}
                disabled={isResending}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded border border-slate-300 bg-white hover:bg-slate-50 text-slate-600 disabled:opacity-50"
                data-testid="fax-resend-button"
              >
                <RefreshCw className={`w-3 h-3 ${isResending ? 'animate-spin' : ''}`} />
                Resend
              </button>
            )}
          </div>
        )}
      </div>

      {/* Inline upload — FAX-DROPZONE-001 premium drag & drop (same endpoint / fields / limits) */}
      {showUpload && (
        <FaxResponseDropzone
          uploadUrl={`${API}/orders/${orderId}/fax/received`}
          faxEntryId={entry.id}
          onSuccess={(data) => {
            toast.success(data.message || 'Fax marked received and document uploaded');
            if (data.fax_deliveries && onEntriesUpdate) onEntriesUpdate(data.fax_deliveries);
            if (onUpdate) onUpdate();
          }}
          onDone={() => setShowUpload(false)}
          onCancel={() => setShowUpload(false)}
        />
      )}

      {/* Details */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600">
        <div>
          <span className="text-slate-400">Recipient:</span>{' '}
          <span data-testid="fax-delivery-recipient">{entry.recipient || entry.fax_number || '—'}</span>
        </div>
        <div>
          <span className="text-slate-400">Sent:</span>{' '}
          <span data-testid="fax-delivery-sent-at">{formatTime(entry.sent_at)}</span>
        </div>
        <div>
          <span className="text-slate-400">By:</span>{' '}
          <span data-testid="fax-delivery-sent-by">{entry.sent_by_name || '—'}</span>
        </div>
        <div>
          <span className="text-slate-400">Attempts:</span>{' '}
          <span data-testid="fax-delivery-attempts">{entry.attempt_count || 1}</span>
        </div>
        {entry.status === 'received' && entry.received_at && (
          <div className="col-span-2 mt-1">
            <span className="text-teal-500">Received:</span>{' '}
            <span data-testid="fax-delivery-received-at">{formatTime(entry.received_at)}</span>
            {entry.received_by_name && (
              <span className="text-slate-400"> by {entry.received_by_name}</span>
            )}
          </div>
        )}
        {entry.error && entry.status !== 'received' && (
          <div className="col-span-2 text-red-600 mt-1">
            <span className="text-red-400">Error:</span> {entry.error}
          </div>
        )}
      </div>

      {/* SES Delivery Tracking */}
      {entry.ses_delivery_status && (() => {
        const sesCfg = SES_STATUS_CONFIG[entry.ses_delivery_status] || SES_STATUS_CONFIG.accepted;
        const SesIcon = sesCfg.icon;
        return (
          <div className="mt-2 space-y-1.5" data-testid="fax-ses-tracking">
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border ${sesCfg.color}`}>
                <SesIcon className="w-2.5 h-2.5" />
                {sesCfg.label}
              </span>
              {entry.ses_delivery_updated_at && (
                <span className="text-[10px] text-slate-400">{formatTime(entry.ses_delivery_updated_at)}</span>
              )}
            </div>
            {entry.ses_bounce_reason && (
              <div className="text-[11px] text-red-600 bg-red-50 rounded px-2 py-1" data-testid="fax-ses-bounce-reason">
                <span className="font-medium">Bounce:</span> {entry.ses_bounce_reason}
              </div>
            )}
            {entry.ses_reject_reason && (
              <div className="text-[11px] text-red-600 bg-red-50 rounded px-2 py-1" data-testid="fax-ses-reject-reason">
                <span className="font-medium">Rejected:</span> {entry.ses_reject_reason}
              </div>
            )}
            {entry.ses_complaint_reason && (
              <div className="text-[11px] text-orange-600 bg-orange-50 rounded px-2 py-1" data-testid="fax-ses-complaint-reason">
                <span className="font-medium">Complaint:</span> {entry.ses_complaint_reason}
              </div>
            )}
            {entry.ses_smtp_response && (
              <div className="text-[10px] text-slate-500 font-mono truncate" title={entry.ses_smtp_response}>
                SMTP: {entry.ses_smtp_response}
              </div>
            )}
          </div>
        );
      })()}

      {/* Tracking Details (on-demand) */}
      {entry.message_id && (
        <div className="mt-2">
          <button
            onClick={showTracking ? () => setShowTracking(false) : handleFetchTracking}
            disabled={isLoadingTracking}
            className="inline-flex items-center gap-1 text-[11px] text-indigo-600 hover:text-indigo-800 hover:underline"
            data-testid="fax-check-delivery-btn"
          >
            {isLoadingTracking ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <RefreshCw className="w-3 h-3" />
            )}
            {showTracking ? 'Hide Tracking' : 'Check Delivery Status'}
          </button>
          {showTracking && trackingData && (
            <div className="mt-1.5 bg-slate-50 border border-slate-200 rounded-md p-2" data-testid="fax-tracking-timeline">
              <p className="text-[10px] font-semibold text-slate-500 mb-1 uppercase tracking-wider">SES Event Timeline</p>
              {trackingData.events && trackingData.events.length > 0 ? (
                <div className="space-y-1">
                  {trackingData.events.map((ev, i) => {
                    const evCfg = SES_STATUS_CONFIG[ev.event_type] || { label: ev.event_type, color: 'bg-slate-50 text-slate-600 border-slate-200', icon: Send };
                    const EvIcon = evCfg.icon;
                    return (
                      <div key={i} className="flex items-center gap-2 text-[11px]">
                        <EvIcon className="w-3 h-3 flex-shrink-0 text-slate-500" />
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${evCfg.color}`}>
                          {evCfg.label}
                        </span>
                        <span className="text-slate-400">{formatTime(ev.timestamp)}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-[11px] text-slate-400 italic">No SES events received yet. Events arrive within minutes of sending.</p>
              )}
              {trackingData.message_id && (
                <p className="text-[9px] text-slate-400 mt-1.5 font-mono break-all">
                  Message ID: {trackingData.message_id}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* PDF links */}
      <div className="flex items-center gap-3 mt-2">
        {entry.file_id && (
          <button
            onClick={() => handleViewPdf(entry.file_id)}
            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
            data-testid="fax-view-pdf-button"
          >
            <FileText className="w-3 h-3" />
            View Fax Request
          </button>
        )}
        {entry.received_file_id && (
          <button
            onClick={() => handleViewPdf(entry.received_file_id)}
            className="inline-flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 hover:underline"
            data-testid="fax-view-received-button"
          >
            <FileText className="w-3 h-3" />
            View Received Fax
          </button>
        )}
      </div>
    </div>
  );
};


// ── Fax Status Summary — real-time aggregate status line ──
const FaxStatusSummary = ({ entries, faxStatus }) => {
  const actionable = entries.filter(e =>
    e.status === 'sent' || e.status === 'received' || e.status === 'sending'
  );
  if (actionable.length === 0) return null;

  const receivedCount = actionable.filter(e => e.status === 'received').length;
  const totalCount = actionable.length;
  const allReceived = receivedCount === totalCount;
  const isReviewed = faxStatus === 'reviewed';

  let message = '';
  let colorClass = '';
  let icon = null;

  if (isReviewed) {
    message = 'All fax files reviewed';
    colorClass = 'bg-green-50 text-green-700 border-green-200';
    icon = <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />;
  } else if (allReceived) {
    message = totalCount === 1
      ? 'Fax received — awaiting processor review'
      : `${receivedCount}/${totalCount} jurisdictions received — awaiting processor review`;
    colorClass = 'bg-blue-50 text-blue-700 border-blue-200';
    icon = <Download className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />;
  } else if (receivedCount > 0) {
    message = `${receivedCount}/${totalCount} jurisdictions received — awaiting remaining fax response`;
    colorClass = 'bg-amber-50 text-amber-700 border-amber-200';
    icon = <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />;
  } else {
    message = totalCount === 1
      ? 'Fax sent — awaiting response'
      : `${totalCount} fax requests sent — awaiting response`;
    colorClass = 'bg-slate-50 text-slate-600 border-slate-200';
    icon = <Send className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />;
  }

  return (
    <div
      className={`flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs font-medium ${colorClass}`}
      data-testid="fax-status-summary"
    >
      {icon}
      <span>{message}</span>
    </div>
  );
};

// ── Main section: renders all fax entries ──
const FaxDeliverySection = ({ orderData, currentUser, onUpdate }) => {
  // Local state for entries — allows immediate UI update from API responses
  // without waiting for parent to re-fetch orderData
  const buildEntries = (data) => {
    let e = data?.fax_deliveries;
    if (!e || !Array.isArray(e) || e.length === 0) {
      const legacy = data?.fax_delivery;
      if (legacy) return [{ ...legacy, id: legacy.id || 'legacy' }];
      return [];
    }
    return e;
  };

  const [entries, setEntries] = useState(() => buildEntries(orderData));

  // Sync with props when orderData changes (e.g., modal reopened with fresh data)
  useEffect(() => {
    setEntries(buildEntries(orderData));
  }, [orderData?.fax_deliveries, orderData?.fax_delivery]);

  // Callback for FaxEntryCard to update entries from API response
  const handleEntriesUpdate = (newDeliveries) => {
    if (Array.isArray(newDeliveries) && newDeliveries.length > 0) {
      setEntries(newDeliveries);
    }
  };

  if (!entries || entries.length === 0) return null;

  const isOnly = entries.length === 1;

  return (
    <div className="mt-3" data-testid="fax-delivery-section">
      <div className="flex items-center gap-2 mb-2">
        <Send className="w-4 h-4 text-slate-500" />
        <span className="text-sm font-semibold text-slate-700">Fax Delivery</span>
        {entries.length > 1 && (
          <span className="text-xs text-slate-400">({entries.length} requests)</span>
        )}
      </div>
      <FaxStatusSummary entries={entries} faxStatus={orderData?.fax_status} />
      <div className="space-y-2 mt-2">
        {entries.map((entry, idx) => (
          <FaxEntryCard
            key={entry.id || idx}
            entry={entry}
            orderId={orderData?.id}
            currentUser={currentUser}
            onUpdate={onUpdate}
            onEntriesUpdate={handleEntriesUpdate}
            isOnly={isOnly}
          />
        ))}
      </div>
    </div>
  );
};

export default FaxDeliverySection;
